import { Box, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryAttestedDocument } from "../../api/queries";
import { isHttpError } from "../../api";
import {
  StoredDocument,
  VerifyFooter,
  VerifyHowTo,
  VerifyMasthead,
  VerifyMetadata,
  VerifyVerdict,
} from "../../components/VerifyDocument";
import {
  AttestedDocument,
  isAttestedDocument,
} from "../../models/attestedDocument";
import { useAuth } from "../../hooks";
import {
  formatDocumentDate,
  getStorageToken,
  REFERENCE_LENGTH,
  retentionMonths,
} from "../../utils";

const DEFAULT_RETENTION_MONTHS = 6;

type Outcome =
  | "loading"
  | "available"
  | "expired"
  | "unknown"
  | "malformed"
  | "throttled"
  | "failed";

const VerifyDocumentPage = () => {
  const { reference = "" } = useParams<{ reference: string }>();
  const { t } = useTranslation();

  // Read through the context, not straight out of localStorage: the provider
  // writes the token after this subtree has already rendered, and only a
  // context change re-renders a subtree whose element never changed.
  const { token: contextToken } = useAuth();
  const token = contextToken?.access_token || getStorageToken();

  const { data, isLoading, isError, error, gate } = useQueryAttestedDocument({
    reference,
    authorisation: token || "",
    enabled: !!token,
  });

  const attested: AttestedDocument | null = isAttestedDocument(data)
    ? data
    : null;

  const outcome = ((): Outcome => {
    if (gate !== "ok") return gate;
    if (isError || error) {
      // A reference that never existed and one purged past its tombstone both
      // answer 404, so neither the page nor a caller can tell them apart.
      const status = isHttpError(error) ? error.status : 0;
      return status === 404 || status === 400 ? "unknown" : "failed";
    }
    if (isLoading) return "loading";

    // A body that resolved but does not match the contract has to land
    // somewhere. Falling back to the spinner would leave a reader watching it
    // forever with nothing to quote to support.
    if (!attested) return "failed";

    return attested.status === "AVAILABLE" ? "available" : "expired";
  })();

  const months = attested
    ? retentionMonths(attested.retention_days)
    : DEFAULT_RETENTION_MONTHS;

  const verdict: Record<Outcome, { title: string; body: string }> = {
    loading: { title: t("verify.loading"), body: "" },
    available: {
      title: t("verify.available.title"),
      body: t("verify.available.body"),
    },
    expired: {
      title: t("verify.expired.title", {
        date: formatDocumentDate(attested?.expires_at),
      }),
      body: t("verify.expired.body", { months }),
    },
    unknown: {
      title: t("verify.unknown.title"),
      body: t("verify.unknown.body", { length: REFERENCE_LENGTH }),
    },
    malformed: {
      title: t("verify.malformed.title"),
      body: t("verify.malformed.body", { length: REFERENCE_LENGTH }),
    },
    throttled: {
      title: t("verify.throttled.title"),
      body: t("verify.throttled.body"),
    },
    failed: {
      title: t("verify.failed.title"),
      body: t("verify.failed.body"),
    },
  };

  return (
    <Box
      sx={theme => ({
        maxWidth: "880px",
        mx: "auto",
        p: "34px 22px 70px",
        backgroundColor: theme.palette.background.default,
      })}
    >
      <VerifyMasthead />

      {outcome === "loading" ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress aria-label={t("verify.loading")} />
        </Box>
      ) : (
        <VerifyVerdict
          title={verdict[outcome].title}
          body={verdict[outcome].body}
          tone={outcome === "available" ? "neutral" : "notice"}
        />
      )}

      {attested && (outcome === "available" || outcome === "expired") && (
        <VerifyMetadata attested={attested} trimmed={outcome === "expired"} />
      )}

      {attested && outcome === "available" && (
        <>
          <StoredDocument attested={attested} />
          <VerifyHowTo reference={attested.reference} />
        </>
      )}

      <VerifyFooter months={months} />
    </Box>
  );
};

export default VerifyDocumentPage;
